import { MessageType } from '@/models/message-type';
import { SessionDetail } from '@/models/session-detail';
import { SessionMessage } from '@/models/session-message';
import * as chatSessionRepo from '@/repositories/chat-session-repo';
import { Button, Container, Header, Link, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import * as luxon from 'luxon';
import Image from 'next/image';
import {useEffect, useState} from 'react';
import styles from '../../styles/chat.module.scss';
import ChatInputPanel, { ChatScrollState } from './chat-input-panel';
import ChatMessage from './chat-message';
import { ChatBotConfiguration } from './types';
import { getRandomPrompts } from '@/repositories/prompt-repo';
//import { initialize as initializeMermaid } from '../mermaid';

export default function Chat(props: {sessionId: string}) {
  const [running, setRunning] = useState<boolean>(false);
  const [session, setSession] = useState<SessionDetail>();
  const [configuration, setConfiguration] = useState<ChatBotConfiguration>(
    () => ({
      streaming: true,
      showMetadata: false,
      maxTokens: 512,
      temperature: 0.6,
      topP: 0.9,
      files: null,
    })
  );
  const [isWaitingForAI, setIsWaitingForAI] = useState<boolean>(false);
  // const messagesEndRef = useRef<HTMLElement|null>(null);

  // Initialize Mermaid.
  // useEffect(() => {
  //   initializeMermaid();
  // }, [])
  //console.log("Initializing mermaid...");
  //initializeMermaid();

  const [messageHistory, setMessageHistory] = useState<SessionMessage[]>([]);
  const [subscription, setSubscription] =
    useState<ZenObservable.Subscription>();

  useEffect(() => {
    // console.log('Message history has changed: ', messageHistory);
  }, [messageHistory]);

  useEffect(() => {
    // if (subscription) {
    //   console.log("You're already subscribed!");
    //   return;
    // }
    // console.log(`Subscribing to ${session?.id}`);

    const onNext = (nxt: chatSessionRepo.NotifyPromptResponse) => {
      nxt.data.notifyPromptResponse.output =
        nxt.data.notifyPromptResponse.output ?? '';

      // console.log('onNext:', nxt.data.notifyPromptResponse);
      // console.log('messageHistory:', messageHistory);

      // console.log('Output:', nxt.data.notifyPromptResponse.output);
      const isComplete = nxt.data.notifyPromptResponse.complete;

      setMessageHistory(messageHistory => {
        if (messageHistory.length < 1) {
          // Create a new message and add it to the history.
          console.log('The history was empty.');
          messageHistory.push({
            datestamp: luxon.DateTime.now(),
            type: MessageType.AI,
            text: nxt.data.notifyPromptResponse.output,
            metadata: {
              isComplete,
              id: 'Z1',
            },
          });
        } else {
          const lastMessage = messageHistory[messageHistory.length - 1];
          // console.log('lastMessage:', lastMessage);
          if (
            lastMessage?.type !== MessageType.AI ||
            lastMessage?.metadata.isComplete
          ) {
            // console.log('Last message was complete.');
            // The last message was complete, so we need to make a new one.
            messageHistory.push({
              datestamp: luxon.DateTime.now(),
              type: MessageType.AI,
              text: nxt.data.notifyPromptResponse.output,
              metadata: {
                isComplete,
                id: 'Z2',
              },
            });
          } else {
            // console.log('Appending to the last message.');

            messageHistory = messageHistory.slice(0, -1);
            messageHistory.push({
              ...lastMessage,
              text:
                lastMessage.text + nxt.data.notifyPromptResponse.output ?? '',
              metadata: {
                isComplete,
                id: 'Z3',
              },
            });
          }
        }

        return messageHistory;
      });

      setIsWaitingForAI(!isComplete);
    };

    setSubscription(
      chatSessionRepo.subscribeToChat(
        session?.id!,
        onNext,
        err => {
          console.log('onError:', err);
        },
        () => {
          console.log('Socket closed...?');
        }
      )
    );
    console.log('Done subscribing.');
  }, [session?.id]);

  const scrollToEnd = () => {
    const elem = document.getElementById('chat_end'); //.scrollIntoView({ behavior: 'smooth' });
    if (!elem) return;

    window.scrollBy({ top: elem.getBoundingClientRect().y - window.innerHeight * 0.9 })
  }

  useEffect(() => {
    if (!isWaitingForAI) {
      scrollToEnd();
    }
  }, [messageHistory, isWaitingForAI]);

  const reloadSession = async () => {
    const sessionId = session?.id ?? props.sessionId;
    console.log('Reloading session:', sessionId);
    let gotSession: SessionDetail | undefined = undefined;
    try {
      gotSession = await chatSessionRepo.getSession(sessionId!);
      console.log('Got session:', gotSession);
    } catch (e) {
      console.log('Creating new session.');
      const summary = await chatSessionRepo.createSession(
        'Untitled Conversation'
      );
      gotSession = await chatSessionRepo.newSessionDetail(summary);
      console.log('Created session:', gotSession);
    }

    if (!gotSession) {
      throw new Error("Unable to generate chat session.");
    }

    setSession(gotSession);

    if (gotSession.messages) {
      ChatScrollState.skipNextHistoryUpdate = true;
      ChatScrollState.skipNextScrollEvent = true;
      setMessageHistory(h => { 
        h = gotSession?.messages ?? [];
        return h;
      });
      console.log('Message history:', gotSession.messages);
    }

    setRunning(false);
    scrollToEnd();
  };

  useEffect(() => {
    // if (!appContext) return;
    // console.log('Resetting message history.');
    setMessageHistory(h => {
      h = [];
      return h;
    });

    if (!props.sessionId) {
      chatSessionRepo.newSessionDetail().then(x => setSession(x));
      return;
    }

    reloadSession();
  }, [props.sessionId]);

  const handleMessageSent = async (message: string) => {
    setIsWaitingForAI(true);
    const newChatRecord = await chatSessionRepo.submitPrompt(
      session?.id ?? "WHOA, UNKNOWN SESSION ID!",
      message
    );
    setMessageHistory(h => {
      h.push({
        datestamp: luxon.DateTime.now(),
        type: MessageType.Human,
        text: message,
        metadata: {
          isComplete: true,
          id: newChatRecord.recordId,
        },
      });
      return h;
    });
    // await reloadSession();
  };

  const onRenameSession = () => {
    if (!session?.id) return;

    // chatSessionRepo.renameSession(session?.id ?? "WHOA, UNKNOWN SESSION ID!", "New Name");

    // Open a dialog window with the Cloudscape Design theme to request a new name from the user.
    const newSessionName = window.prompt("Enter a new session name:");
    if (newSessionName) {
      chatSessionRepo.renameSession(session?.id, newSessionName);
      
      // Update the session name in the UI.
      setSession(s => {
        if (!s) return s;
        s.name = newSessionName;
        return s;
      });
    }
  }

  const choosePrompt = async (prompt: string) => {
    await handleMessageSent(prompt);
  }

  return (
    <div>
      <div style={{ position: "fixed", marginTop: "-90px", zIndex: "2" }}>
        <Container>
          <Header variant="h1" info={<Button variant="link" onClick={onRenameSession}>Rename</Button>}>
            {session?.name ?? "Loading..."}
          </Header>
        </Container>
      </div>
      <div className={styles.chat_container} style={{ marginTop: "90px"}}>
        <SpaceBetween direction="vertical" size="m">
          {messageHistory.map((message, idx) => (
            <ChatMessage
              key={idx}
              message={message}
              configuration={configuration}
              isLoading={isWaitingForAI /*&& (idx === (messageHistory.length - 1))*/}
              onRenderComplete={scrollToEnd}
            />
          ))}
          {isWaitingForAI && (
            <StatusIndicator type="in-progress">Thinking...</StatusIndicator>
          )}
          <div id="chat_end" />
          <div id="chat_footer" style={{ height: '260px' }} />
        </SpaceBetween>
        <div className={styles.welcome_text}>
          {messageHistory.length === 0 && session && (
            <>
              <div>
                <center>
                  <Image src="/AI.svg" alt="AI" width="82" height="82" />
                </center>
              </div>
              <div>
                <center>How can I help you today?</center>
                {getRandomPrompts().map((prompt, idx) =>
                  (<Button variant="link" onClick={() => choosePrompt(prompt)}>{prompt}</Button>))}
              </div>
            </>
          )}
          {!session && (
            <center>
              <StatusIndicator type="loading">Loading session</StatusIndicator>
            </center>
          )}
        </div>
        <div id="input_container" className={styles.input_container}>
          <ChatInputPanel
            session={session}
            running={running}
            setRunning={setRunning}
            messageHistory={messageHistory}
            setMessageHistory={setMessageHistory}
            configuration={configuration}
            setConfiguration={setConfiguration}
            onMessageSent={handleMessageSent}
            isWaitingForAI={isWaitingForAI}
          />
        </div>
      </div>
    </div>
  );
}
